<template>
  <Loader
    :value="promise"
    class="shop"
    :class="{ 'shop--is-summary': !category }"
    theme="default"
    :message="$t('loading', { resource: $tc('addonShop').toLowerCase() }) + '...'"
  >
    <template v-if="data.id">
      <div class="shop__content">
        <Navigation
          v-model="navigation"
          :items="categories"
          :selected-items="selectedItems"
          :summary="summary"
          :sets="hasSets"
        />

        <ProductDetails v-if="category" :value="product" @open:modal="openProductDetailsModal">
          <template #body-bottom>
            <div v-if="floorplan" class="shop-floorplan-wrapper">
              <label>{{ $t('youAreHere') }}:</label>
              <Floorplan :image="floorplan.image.url" :value="floorplan.data" />
            </div>
          </template>
        </ProductDetails>

        <Summary v-if="summary && navigation === 'summary'" :value="data" />
        <Sets
          v-if="navigation === 'sets'"
          :value="data"
          @select="selectSet"
          @open:modal="openProductDetailsModal"
        />

        <Footer
          v-if="category"
          :value="data"
          :hasNext="hasNext"
          :hasPrev="hasPrev"
          @next="next"
          @prev="prev"
        >
          <template #action>
            <slot name="action" />
          </template>
        </Footer>
      </div>
      <div v-if="category" class="shop__sidebar">
        <div class="shop__sidebar-description">
          <div v-if="category.description" v-html="category.description"></div>
          <template v-else>
            <h2>{{ $tc('shopPresentationSidebarDescriptionTitle') }}</h2>
            <p>{{ $tc('shopPresentationSidebarDescriptionContent') }}</p>
          </template>
        </div>
        <Subcategory
          v-for="(item, index) in subcategories"
          :key="index"
          :value="item"
          :category="category"
          :sets="sets"
          :selected-items="selectedItems"
          :readonly="['signed', 'accepted', 'expired'].includes(category.status)"
          :active="subcategory === item"
          @open:modal="openProductDetailsModal"
          @update:active="subcategory = item"
          @select="select"
          @unselect="$ev => unselect($ev, false, { save: true })"
        />
      </div>
      <Modal
        :show="showproductDetails"
        @close="closeProductDetailsModal"
        class="shop__modal"
        clickaway
      >
        <Card theme="flat" class="shop__modal-content">
          <template #default>
            <ProductDetails
              v-if="currentProduct"
              :value="currentProduct"
              @open:modal="openProductDetailsModal"
            >
              <template #body-bottom>
                <div v-if="floorplan" class="shop-floorplan-wrapper">
                  <label>{{ $t('youAreHere') }}:</label>
                  <Floorplan :image="floorplan.image.url" :value="floorplan.data" />
                </div>
              </template>
            </ProductDetails>
          </template>
          <template #footer>
            <ButtonComponent
              :label="$t('close')"
              @click="closeProductDetailsModal"
              class="shop__modal-close-action"
              :icon="['fal', 'times']"
              theme="primary"
            />
          </template>
        </Card>
      </Modal>
    </template>
  </Loader>
</template>

<script>
import { LoaderComponent as Loader } from 'vue-elder-loader'
import Navigation from './Navigation'
import Subcategory from './Subcategory'
import Footer from './Footer'
import Floorplan from './Floorplan'
import Summary from './Summary'
import ProductDetails from './Product/Details'
import { ModalComponent as Modal } from 'vue-elder-modal'
import { CheckConditions, getConditionalDependencies, ConditionFilter } from './utils'
import { getShop, selectItem, unselectItem } from './api'
import Card from '@kvass/card'
import Sets from './Sets/index'

export default {
  props: {
    id: String,
    summary: {
      type: Boolean,
      default: true,
    },
  },
  inject: ['accessToken'],
  provide() {
    return {
      selectedItems: () => this.selectedItems,
      getItemStatus: this.getItemStatus,
    }
  },
  watch: {
    id: {
      handler: 'fetch',
      immediate: true,
    },
    category: {
      handler: 'setSubcategory',
    },
    navigation: {
      handler(val, oldVal) {
        if (!oldVal) return
        this.$nextTick(() => this.$el.scrollIntoView({ behavior: 'smooth' }))
      },
    },
  },
  data() {
    return {
      showproductDetails: false,
      currentProduct: null,
      promise: null,
      data: {},
      navigation: null,
      subcategory: null,
    }
  },
  computed: {
    category: {
      get() {
        if (!this.data.categories) return
        return this.data.categories.find(c => c.id === this.navigation)
      },
      set(val) {
        return (this.navigation = val.id)
      },
    },
    hasSets() {
      return Boolean(this.data.sets && this.data.sets.length)
    },
    floorplan() {
      if (!this.category || !this.category.floorplans.length) return

      return this.category.floorplans
        .filter(f => CheckConditions(f, this.selectedItems))
        .find(Boolean)
    },
    product() {
      if (!this.subcategory) return
      return (this.subcategory.items || []).find(item => item.selected)
    },
    categories() {
      return this.data.categories.filter(c => ConditionFilter(c, this.selectedItems))
    },
    subcategories() {
      if (!this.category) return []
      return this.category.categories.filter(
        v => ConditionFilter(v, this.selectedItems) && v.items.length,
      )
    },
    selectedItems() {
      let items = []

      this.data.categories.forEach(cat => {
        cat.categories.forEach(subcat => {
          subcat.items.forEach(item => {
            if (!item.selected) return
            items.push(item)
          })
        })
      })

      return items
    },
    hasNext() {
      return this.next(true)
    },
    hasPrev() {
      return this.prev(true)
    },
    sets() {
      if (!this.data) return
      return this.data.sets.map(set => {
        let items = this.getSetItems(set.id)
        let selected = this.selectedItems.filter(s => s.sets.includes(set.id))

        return {
          ...set,
          score: selected.length / items.length,
        }
      })
    },
  },
  methods: {
    openProductDetailsModal(item) {
      this.currentProduct = item
      this.showproductDetails = true
    },
    closeProductDetailsModal() {
      this.showproductDetails = false
    },
    getSetItems(set) {
      return this.data.categories.reduce((productLinks, category) => {
        category.categories.forEach(subcategory => {
          subcategory.items
            .filter(i => (i.sets || []).includes(set))
            .forEach(link => productLinks.push(link))
        })

        return productLinks
      }, [])
    },
    getItem(id) {
      let result = {}

      this.data.categories.find(category => {
        if (!category || !category.categories) return

        return category.categories.find(subcategory => {
          if (!subcategory || !subcategory.items) return

          let item = subcategory.items.find(item => item.id === id)
          if (!item) return

          result = { item, subcategory, category }
        })
      })

      return result
    },
    next(dry) {
      if (this.subcategory) {
        let subindex = this.subcategories.findIndex(c => c === this.subcategory)
        let nextsub = this.subcategories[subindex + 1]

        if (nextsub) {
          if (!dry) this.subcategory = nextsub
          return true
        }
      }

      let catindex = this.categories.findIndex(c => c === this.category)
      let nextcat = this.categories[catindex + 1]

      if (nextcat) {
        if (!dry) this.category = nextcat
        return true
      }

      if (this.summary && this.category) {
        if (!dry) this.navigation = 'summary'
        return true
      }

      return false
    },
    prev(dry) {
      if (this.subcategory) {
        let subindex = this.subcategories.findIndex(c => c === this.subcategory)
        let prevsub = this.subcategories[subindex - 1]

        if (prevsub) {
          if (!dry) this.subcategory = prevsub
          return true
        }
      }

      if (!this.category) {
        if (!dry) this.category = this.categories[this.categories.length - 1]
        return true
      }

      let catindex = this.categories.findIndex(c => c === this.category)
      let prevcat = this.categories[catindex - 1]

      if (prevcat) {
        if (!dry) this.category = prevcat
        return true
      }

      if (this.hasSets) {
        if (!dry) this.navigation = 'sets'
        return true
      }

      return false
    },
    setSubcategory() {
      if (!this.category) return (this.subcategory = null)
      this.subcategory = this.category.categories[0]
    },
    fetch() {
      if (!this.id) return
      return (this.promise = getShop({ id: this.id }).then(data => {
        this.data = data
        if (!this.navigation) {
          if (this.data.sets.length) return (this.navigation = 'sets')
          return (this.category = this.data.categories[0])
        }
      }))
    },
    getItemStatus(id) {
      let defaultState = { isSelectable: true, isUnselectable: true, dependencies: [] }
      if (!id) return defaultState

      let { item, subcategory, category } = this.getItem(id)
      if (!item) return defaultState

      let selected = this.selectedItems
      let lockedStatuses = ['accepted', 'signed', 'expired']
      let dependencies = getConditionalDependencies(id, this.data)

      let selectConditions = [
        CheckConditions(category, selected),
        CheckConditions(subcategory, selected),
        CheckConditions(item, selected),
        !lockedStatuses.includes(category.status),
        dependencies.ifnot.every(i => {
          let { category } = this.getItem(i)
          return !category || !lockedStatuses.includes(category.status)
        }),
      ]

      let unselectConditions = [
        !lockedStatuses.includes(category.status),
        dependencies.if.every(i => {
          let { category } = this.getItem(i)
          return !category || !lockedStatuses.includes(category.status)
        }),
      ]

      return {
        isSelectable: selectConditions.every(Boolean),
        isUnselectable: unselectConditions.every(Boolean),
        dependencies,
      }
    },
    select(id) {
      let { subcategory, item } = this.getItem(id)
      let { isSelectable, dependencies } = this.getItemStatus(id)

      if (!isSelectable || item.selected) return false

      // Unselect all conditionals
      let depOperations = dependencies.ifnot.map(i => this.unselect(i, true))
      if (!depOperations.every(Boolean)) return false

      // Perform selection
      let itemsOperations = subcategory.items.map(i => {
        if (i !== item) return this.unselect(i.id)
        return true
      })
      if (!itemsOperations.every(Boolean)) return false

      item.selected = true

      // Send to server
      if (this.accessToken) selectItem(id, { accessToken: this.accessToken })

      return true
    },
    unselect(id, setDefault, options = {}) {
      let { save = false } = options

      let { subcategory, item } = this.getItem(id)
      if (!subcategory) return true

      let { isUnselectable, dependencies } = this.getItemStatus(id)

      if (!isUnselectable) return false

      // Unselect all conditionals
      let operations = dependencies.if.map(i => this.unselect(i, true))
      if (!operations.every(Boolean)) return false

      // Perform unselection
      item.selected = false
      if (setDefault)
        subcategory.items.forEach(i => {
          if (!i.isDefault) return
          i.selected = true
        })

      // Send to server
      if (this.accessToken && save) unselectItem(id, { accessToken: this.accessToken })

      return true
    },
    selectSet(ids) {
      if (ids) ids.forEach(id => this.select(id))
      return (this.category = this.data.categories[0])
    },
  },
  components: {
    Loader,
    Navigation,
    Subcategory,
    Footer,
    ProductDetails,
    Summary,
    Modal,
    Card,
    Floorplan,
    Sets,
  },
}
</script>

<style lang="scss">
@import './styles/_variables';

.shop {
  $spacing: 3rem;
  $spacingResponsive: 2rem 1.5rem;
  $sidebarWidth: 500px;
  $productDetailsLayer: 2;

  position: relative;
  z-index: 1002;
  background-color: white;

  max-height: 100vh;

  border-top: 1px solid $border-color;
  border-bottom: 1px solid $border-color;

  display: flex;

  @media (max-width: $shop-responsive) {
    flex-direction: column;
  }

  &--is-summary {
    .shop__content {
      width: 100%;
    }
  }

  &-summary {
    padding: $spacing;
    @media (max-width: $shop-responsive) {
      padding: 1rem;
    }
  }

  &-navigation,
  &-footer,
  &-product-details {
    position: sticky;

    @media (max-width: $shop-responsive) {
      z-index: $productDetailsLayer;
    }
  }

  &-navigation {
    top: 0;
  }

  &-footer {
    bottom: 0;

    @media (max-width: $shop-responsive) {
      order: 3;
    }
  }

  &-product-details {
    flex-grow: 1;
    padding: $spacing;

    @media (max-width: $shop-responsive) {
      padding: 1.5rem;
    }
  }

  &__sidebar,
  &__content {
    max-height: 100vh;
    overflow-y: auto;

    display: flex;
    flex-direction: column;

    @include scrollShadow();
  }

  &__content {
    width: calc(100% - #{$sidebarWidth});

    @media (max-width: $shop-responsive) {
      display: contents;
      width: 100%;
    }

    .shop-product-details {
      @media (max-width: $shop-responsive) {
        display: none;
      }
    }
  }

  &__sidebar {
    width: $sidebarWidth;
    border-left: 1px solid $border-color;

    &-description {
      font-size: 1.2em;
      padding: $spacing $spacing 0;
      opacity: 0.7;

      h2 {
        font-size: 1.2em;
      }
      p {
        margin: 0.5rem 0;
      }

      @media (max-width: $shop-responsive) {
        padding: $spacingResponsive;
        h2 {
          font-size: 1em;
        }
      }
    }

    .shop-subcategory {
      @media (max-width: $shop-responsive) {
        padding: $spacingResponsive;
      }
    }

    @media (max-width: $shop-responsive) {
      width: initial;
    }
  }

  &__modal {
    display: flex;
    padding: 0;

    & > .kvass-card {
      width: 80vw;
      height: 80vh;
      border-radius: 0;
      overflow: auto;
      max-height: initial;

      @media (max-width: $shop-responsive) {
        width: 100vw;
        height: 100vh;
      }

      & > .kvass-card__content {
        padding: 0;
      }
    }

    .shop-product-details {
      &__header-wrapper {
        @media (max-width: $shop-responsive) {
          margin: calc(var(--kvass-card-spacing) * -1);
        }
      }
    }

    .kvass-card__footer {
      padding: 1.5rem 1rem;
      display: flex;
      justify-content: center;
      position: sticky;
      bottom: 0;
      z-index: calc(1 + #{$productDetailsLayer});
    }
  }

  a {
    word-break: break-all;
  }
}
</style>
